import React, { useEffect, useCallback, useMemo } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { debateCountDown, startDebate } from '../../../redux/Thunks/debatesThunk';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTimeZoneDate } from '../../common/set_date';

const DebateTimer = React.memo(({ finished, hasStarted }) => {

  const { debate_id } = useParams();
  const dispatch = useDispatch();


  const [timerState, setTimerState] = useState({
    timeRemaining: null,
    isActive: false,
    debateStarted: false,
    morethan23h: false,
    color: 'text-green-500'
  });
  const [error, setError] = useState(null);
  const [backendInterval, setBackendInterval] = useState(90000) // 1 minute 30 seconds

  // Memoize color calculation
  const getColorForTime = useCallback((remainingTime) => {
    if (remainingTime <= 120) return 'text-red-600';  //Less than 2 minutes
    if (remainingTime <= 300) return 'text-orange-400'; //Less than 5 minutes
    return 'text-green-500';
  }, []);

  // Memoize time calculations
  const timeDisplay = useMemo(() => {
    if (timerState.timeRemaining === null) return { hours: 0, minutes: 0, seconds: 0 };
    const hours = Math.floor(timerState.timeRemaining / 3600);
    const minutes = Math.floor((timerState.timeRemaining % 3600) / 60);
    const seconds = timerState.timeRemaining % 60;
    return { hours, minutes, seconds };
  }, [timerState.timeRemaining]);


  const fetchRemainingTime = useCallback(async () => {
    try {
      const { payload } = await dispatch(debateCountDown({ debate_id }));
      
      if (payload?.time_remaining) {
        const startTime = payload?.start_at ? new Date(payload.start_at) : null;
        const now = new Date();
        const waitTimeMs = startTime ? Math.max(0, startTime - now) : 0;
        const currentWaitTime = Math.floor(waitTimeMs / 1000);

        if (payload?.start_at) {
          const [hours, minutes, seconds] = payload.duration.split(':').map(Number);
          const durationInSeconds = (hours * 3600) + (minutes * 60) + seconds;

          let newState = {
            isActive: payload.is_active,
            morethan23h: currentWaitTime > 82800 ? payload.start_at : false,
            debateStarted: waitTimeMs <= 0
          };

          if (waitTimeMs > 0) { //Debate hasn't started
            newState.timeRemaining = currentWaitTime;
          } else { //Debate has started
            setBackendInterval(10000) // set to 10 seconds interval
            if(!hasStarted){
              dispatch(startDebate({debate_id})) //Start the debate API
            }

            const elapsedSinceStart = Math.floor((now - startTime) / 1000);
            const remainingDebateTime = Math.max(0, durationInSeconds - elapsedSinceStart);
            newState.timeRemaining = remainingDebateTime;
            newState.color = getColorForTime(remainingDebateTime);
          }

          setTimerState(prev => ({
            ...prev,
            ...newState
          }));
        }
      }
    } catch (err) {
      setError('Error fetching debate timer');
      toast.error("Error fetching debate timer");
      console.error('Timer fetch error:', err);
    }
  }, [debate_id, dispatch, getColorForTime, hasStarted]);

  


  useEffect(() => {
   
    
    if(!finished){
      fetchRemainingTime();
      const pollInterval = setInterval(fetchRemainingTime, backendInterval);
    
      const countdownInterval = setInterval(() => {
        setTimerState(prev => {
          if (!prev.isActive || prev.timeRemaining <= 0) return prev;
          const newTimeRemaining = prev.timeRemaining - 1;
          return {
            ...prev,
            timeRemaining: newTimeRemaining,
            color: getColorForTime(newTimeRemaining)
          };
        });
      }, 1000)
  
  
        return () => {
          clearInterval(pollInterval);
          clearInterval(countdownInterval);
        };
    }
    


  }, [fetchRemainingTime, getColorForTime, finished, hasStarted, backendInterval]);


  const formatTime = useCallback((value) => String(value).padStart(2, '0'), []);

  // if server error
  if (error) {
    return <div className="text-red-500 font-medium">{error}</div>;
  }

    // Debate has ended
  if (!timerState.isActive || finished) {
    return <div className="text-red-600 font-medium text-lg">Debate over</div>;
  }

    // // If server hasn't fetched details
    // if (timerState.timeRemaining === null) {
    //   return <div className="text-gray-500">Loading timer...</div>;
    // }

  // Debate hasn't started
  if (!timerState.debateStarted) {
    return (
      <div className="text-yellow-500 font-medium px-4">
        {timerState.morethan23h ? (
          <p className="text-white text-center">
            STARTS AT: <b>{getTimeZoneDate(timerState.morethan23h)}</b>
          </p>
        ) : (
          <div className="text-center">
            Debate hasn't started yet.
            <p className="font-bold text-3xl">
              {`${formatTime(timeDisplay.hours)} : ${formatTime(timeDisplay.minutes)} : ${formatTime(timeDisplay.seconds)}`}
            </p>
            <p>until debate starts</p>
          </div>
        )}
      </div>
    );
  }

  // Debate is ongoing
  return (
    <div className={`h-[60px] flex items-center justify-center border-nightModeDark
      px-4 bg-nightModeDark_600 ${timerState.color} rounded-sm shadow-md shadow-nightModeDark_200`}>
      <p className="font-bold text-3xl">
        {`${formatTime(timeDisplay.hours)} : ${formatTime(timeDisplay.minutes)} : ${formatTime(timeDisplay.seconds)}`}
      </p>
    </div>
  );
});

DebateTimer.displayName = 'DebateTimer';

export default DebateTimer;